import { observable, makeObservable, action, computed } from 'mobx';
import dayjs from 'dayjs';
import { AuthenticationResult, InteractionRequiredAuthError, IPublicClientApplication } from '@azure/msal-browser';
import { Dictionary, JwtTokenHelper } from '@fos/core';

import { environmentStore } from './EnvironmentStore';

export const SECOND = 1000;
export const MINUTE = 60 * SECOND;
export const HOUR = 60 * MINUTE;
export const DAY = 24 * HOUR;

export class AccessTokenStore {
  tokens: Dictionary<string> & { value: string } = { value: null };
  tokenAppRoles: Dictionary<string[]> = {};
  loaded: boolean = false;
  msalInstance: IPublicClientApplication = null;
  hasGodRights: boolean = false;

  private tokenRefreshTimers: { [appId: string]: number } = {};

  constructor() {
    makeObservable(this, {
      tokens: observable.ref,
      tokenAppRoles: observable.ref,
      hasGodRights: observable.ref,
      loaded: observable,

      appRoles: computed,
      hasFullEditRights: computed,

      setToken: action,
      setIsLoaded: action,
      setHasGodRights: action,
      initialize: action,
    });
  }

  initialize() {
    const { baseApi } = environmentStore.environment.appIds;
    this.msalInstance = environmentStore.msal;
    this.loadToken(baseApi);
  }

  async loadToken(appId: string) {
    const scopes = [`${appId}/user_impersonation`];
    const token = await this.getToken(scopes);
    if (!token) {
      this.loginRedirect(scopes);
      return;
    }
    this.setToken(appId, token);
    this.setTokenRefreshTimer(appId, token);
  }

  setToken(appId: string, token: AuthenticationResult) {
    if (appId === environmentStore.environment.appIds.baseApi) {
      this.tokens.value = token.accessToken;
    } else {
      this.tokens[appId] = token.accessToken;
    }
    this.tokenAppRoles = {
      ...this.tokenAppRoles,
      [appId]: JwtTokenHelper.getInfo(token.accessToken).roles,
    };
    this.setIsLoaded();
  }

  setIsLoaded() {
    this.loaded = Object.values(this.tokens).every((token) => !!token);
  }

  setHasGodRights(username: string) {
    this.hasGodRights = username === 'nikita.ivanov3@wartsila.com';
  }

  get appRoles(): string[] {
    return [].concat(...Object.values(this.tokenAppRoles));
  }

  get hasFullEditRights(): boolean {
    return this.appRoles.includes('logabstracteditor-full-edit');
  }

  private getToken = async (scopes: string[]) => {
    const account = this.msalInstance.getActiveAccount() || this.msalInstance.getAllAccounts()[0];

    if (!account) {
      return null;
    }

    this.setHasGodRights(account.username);

    const requestParams = {
      scopes,
      account,
    };

    return await this.msalInstance.acquireTokenSilent(requestParams).catch(async (error) => {
      if (error instanceof InteractionRequiredAuthError) {
        return await this.msalInstance.acquireTokenRedirect(requestParams).catch(() => {
          return null;
        });
      }
      return null;
    });
  };

  private loginRedirect = (scopes: string[]) => {
    this.msalInstance.loginRedirect({
      scopes,
      redirectUri: '/',
    });
  };

  private setTokenRefreshTimer(appId: string, token: AuthenticationResult) {
    // To avoid expired already, renewing token one minute before it expires
    const delay = Math.max(MINUTE / 2, dayjs(token.expiresOn).diff(dayjs().subtract(1, 'minute')));
    if (this.tokenRefreshTimers[appId]) {
      clearTimeout(this.tokenRefreshTimers[appId]);
    }
    this.tokenRefreshTimers[appId] = window.setTimeout(() => this.loadToken(appId), delay);
  }
}

export const accessTokenStore = new AccessTokenStore();
